<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { useAuth } from 'vue-auth3';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useCookies } from 'vue3-cookies';
import axios from 'axios';

import efForm from '../../components/form/efForm.vue';
import efInput from '../../components/efInput.vue';
import efInputPassword from '../../components/efInputPassword.vue';
import efOTPInput from '../../components/efOTPInput.vue';
import efPromiseButton from '../../components/efPromiseButton.vue';
import TwoFactorEnrollment from '../../components/user/TwoFactorEnrollment.vue';
import { apiErrorMessage } from '../../utils/errors';

import type { TwoFactorEnrollment as Enrollment } from '../../types';

const { t } = useI18n();
const auth = useAuth();
const router = useRouter();
const { cookies } = useCookies();

// Storage key for the last e-mail used with "Stay signed in", so it can be
// pre-filled the next time the login page is opened (e.g. after the token expired).
const LAST_EMAIL_KEY = 'auth_last_email';

// The page the user was sent to the login from. The trip to an SSO provider leaves the SPA, so
// `redirectedFrom` is gone once the callback returns and has to wait here in the meantime.
const SSO_TARGET_KEY = 'auth_sso_target';

const loading = ref<boolean>(true);
const error = ref<string | null>(null);
const unauthorized = ref<boolean | null>(null);

// Which half of the form is on screen. The second factor replaces the credentials rather than
// covering them: a dialog over a login form reads as an interruption, and this is one step of the
// same sign in.
// `two_factor_setup` is the enrolment a permission forces on a user without a factor: the QR code
// above the same code field, and the first code both confirms the factor and signs in.
const step = ref<'credentials' | 'two_factor' | 'two_factor_setup'>('credentials');
const code = ref<string>('');
const enrollment = ref<Enrollment | null>(null);

const stepTexts = computed(() => {
	switch (step.value) {
		case 'two_factor':
			return { title: 'pages.auth.login.two_factor.title', text: 'pages.auth.login.two_factor.text' };
		case 'two_factor_setup':
			return { title: 'pages.auth.login.two_factor_setup.title', text: 'pages.auth.login.two_factor_setup.text' };
		default:
			return { title: 'pages.auth.login.title', text: 'pages.auth.login.text' };
	}
});

const data = ref<{
	email: string | null;
	password: string | null;
	remember: boolean | null;
}>({
	email: null,
	password: null,
	remember: null,
});

const isValid = computed(() => {
	return data.value.email && data.value.password;
});

const selfRegistrationEnabled = computed<boolean>(() => {
	return globalThis.env?.app?.self_registration === true;
});

type OAuthService = {
	id?: number;
	key: string;
	icon: string;
	redirect?: string;
};

const oauth = ref<Record<string, OAuthService>>({
	/*google: { id: undefined, key: 'google', icon: 'google.svg' },
  apple: { id: undefined, key: 'apple', icon: 'apple.svg' },
  keycloak: { id: undefined, key: 'keycloak', icon: 'keycloak.svg' },*/
});

onMounted(async () => {
	// Setup the OAuth2 services
	if (globalThis.env?.sso?.microsoft_redirect_url != '') {
		oauth.value['microsoft'] = {
			id: 1,
			key: 'microsoft',
			icon: 'microsoft.svg',
			redirect: globalThis.env?.sso?.microsoft_redirect_url,
		};
	}

	if (globalThis.env?.sso?.authentik_redirect_url != '') {
		oauth.value['authentik'] = {
			id: 1,
			key: 'authentik',
			icon: 'authentik.svg',
			redirect: globalThis.env?.sso?.authentik_redirect_url,
		};
	}

	// The provider accepted the login but the backend refused it. Drop the auto-login, or the
	// page would send the user straight back into the same refusal.
	const ssoError = router.currentRoute.value.query?.sso_error;
	if (ssoError) {
		cookies.remove('loginMethod');
		const messages: Record<string, string> = {
			identity_conflict: 'pages.auth.login.error.sso_identity_conflict',
			locked: 'pages.auth.login.error.sso_locked',
			wrong_tenant: 'pages.auth.login.error.sso_wrong_tenant',
		};
		error.value = t(messages[ssoError as string] ?? 'pages.auth.login.error.sso_rejected');
		loading.value = false;
		return;
	}

	// Redirected back from an OAuth2 provider. `sso` names the provider, which the auto-login
	// cookie remembers for the next visit.
	if (router.currentRoute.value.query?.token) {
		const provider = router.currentRoute.value.query.sso as string | undefined;
		// Stay signed in, so a link opened in a new tab finds the session.
		localStorage.setItem(auth.options.staySignedInKey, JSON.stringify(true));
		auth.token(null, router.currentRoute.value.query.token as string, false);
		auth.fetch().then(() => {
			if (provider && oauth.value[provider]) {
				cookies.set('loginMethod', provider);
			}
			const target = takeSsoTarget();
			router.push(target ? { path: target } : { name: 'Home' });
		});
	}

	// Check for autologin
	else if (cookies.get('loginMethod') && oauth.value[cookies.get('loginMethod')]) {
		const methode = cookies.get('loginMethod');
		oauth_login(oauth.value[methode]);
	}

	// Disable loading
	else {
		// Pre-fill the last e-mail if the user previously chose to stay signed in.
		const lastEmail = localStorage.getItem(LAST_EMAIL_KEY);
		if (lastEmail) {
			data.value.email = lastEmail;
			data.value.remember = true;
		}
		loading.value = false;
	}
});

// Define the login method
async function login() {
	// Purge any leftover token in sessionStorage or localStorage before logging in.
	auth.token(null, null);

	return attempt();
}

/** Send the code the second factor asked for, together with the credentials it belongs to. */
function submitCode(): Promise<void> {
	return attempt(code.value);
}

/** Mint the secret for the forced enrolment, against the credentials still in the form. */
function startEnrollment(): Promise<void> {
	return axios
		.post<Enrollment>('auth/two-factor/setup', {
			email: data.value.email,
			password: data.value.password,
		})
		.then((res) => {
			enrollment.value = res.data;
		})
		.catch((e) => {
			error.value = apiErrorMessage(e);
		});
}

/**
 * Back to the credentials. No token was issued, so there is nothing to undo - but the password
 * goes with it: abandoning a sign in should not leave the credential sitting in the form.
 */
function cancelTwoFactor(): void {
	step.value = 'credentials';
	code.value = '';
	enrollment.value = null;
	error.value = null;
	unauthorized.value = null;
	data.value.password = null;
}

/**
 * One trip to `auth/login`, with the code the second factor asked for on the retry.
 *
 * The backend issues no token until the code is in, so the password is sent again with it. There
 * is nothing half-authenticated to hold on either side, and a code that is rejected costs the
 * same rate limit slot as a wrong password - which is what bounds guessing six digits.
 */
function attempt(twoFactorCode?: string): Promise<void> {
	// Reset the messages
	error.value = null;
	unauthorized.value = null;

	const redirectPath = router.currentRoute.value.redirectedFrom?.fullPath;
	const remember = data.value.remember ?? false;

	// Try to authenticate. "Stay signed in" controls whether the token is
	// persisted (localStorage) or session-only (sessionStorage, cleared on close).
	return auth
		.login({
			data: {
				email: data.value.email,
				password: data.value.password,
				...(twoFactorCode ? { two_factor_code: twoFactorCode } : {}),
			},
			staySignedIn: remember,
			redirect: redirectPath ? { path: redirectPath } : { name: 'Home' },
		})
		.then(() => {
			// Remember the e-mail for the next visit, or forget it when opted out.
			if (remember) {
				localStorage.setItem(LAST_EMAIL_KEY, data.value.email ?? '');
			} else {
				localStorage.removeItem(LAST_EMAIL_KEY);
			}
		})
		.catch((err) => {
			// A second factor stands between the password and the token. The password itself was
			// accepted here, so this is not a failed login - it is the next step of one, and the
			// form swaps over to ask for the code.
			if (err.response?.status === 401 && err.response?.data?.two_factor_required) {
				step.value = 'two_factor';
				code.value = '';

				if (err.response.data.two_factor_invalid === true) {
					error.value = t('pages.auth.login.error.invalid_two_factor_code');
				}

				return;
			}

			// No factor yet, but a permission of this user demands one. A rejected code keeps the
			// secret already on screen - the backend keeps the pending one, and minting a new one
			// would invalidate what the user just scanned.
			if (err.response?.status === 401 && err.response?.data?.two_factor_setup_required) {
				step.value = 'two_factor_setup';
				code.value = '';

				if (err.response.data.two_factor_invalid === true) {
					error.value = t('pages.auth.login.error.invalid_two_factor_code');
				}

				if (!enrollment.value) {
					return startEnrollment();
				}

				return;
			}

			if (err.response?.status === 401) {
				unauthorized.value = true;
			} else if (err.response?.status === 429) {
				// Throttled by the login rate limiter, see LibServiceProvider::registerRateLimiters
				error.value = t('pages.auth.login.error.too_many_requests');
			} else {
				console.warn(err);
				error.value = t('pages.auth.login.error.service_unavailable');
			}
		});
}

/**
 * The page stored before leaving for the SSO provider, read once. Only an app path counts, so a
 * tampered value cannot send the user off to another origin.
 */
function takeSsoTarget(): string | null {
	const target = sessionStorage.getItem(SSO_TARGET_KEY);
	sessionStorage.removeItem(SSO_TARGET_KEY);

	return target?.startsWith('/') && !target.startsWith('//') && !target.startsWith('/login') ? target : null;
}

// Define the login method
function oauth_login(service: OAuthService) {
	const target = router.currentRoute.value.redirectedFrom?.fullPath;
	if (target) {
		sessionStorage.setItem(SSO_TARGET_KEY, target);
	} else {
		sessionStorage.removeItem(SSO_TARGET_KEY);
	}

	return new Promise<void>(() => {
		globalThis.location.href = service.redirect || '/';
		// Wait forever
	});
}
</script>

<template>
	<div class="row justify-content-center" style="max-width: 512px">
		<div v-if="loading" class="d-flex flex-column align-items-center justify-content-center">
			<div class="spinner-border text-primary">
				<span class="visually-hidden">{{ t('pages.auth.login.loading') }}</span>
			</div>
		</div>

		<div v-else class="d-flex flex-column align-items-center justify-content-center">
			<div class="pt-4 pb-2">
				<h5 class="card-title text-center pb-0 fs-4">{{ t(stepTexts.title) }}</h5>
				<p class="text-center small">{{ t(stepTexts.text) }}</p>
			</div>

			<div v-if="unauthorized" class="col-12">
				<div class="alert alert-danger" role="alert">{{ t('pages.auth.login.error.invalid_login') }}</div>
			</div>

			<div v-if="error" class="col-12">
				<div class="alert alert-danger" role="alert">{{ error }}</div>
			</div>

			<!-- Every field here is required, so the stars would tell nobody anything -->
			<efForm hide-required-marks class="w-100">
				<form @submit.prevent="" class="row g-3 w-100">
					<template v-if="step === 'credentials'">
						<div class="col-12">
							<efInput
								v-model="data.email"
								label="pages.auth.login.email"
								autocomplete="username"
								required
							>
								<template #input-group-prepend>@</template>
							</efInput>
						</div>

						<div class="col-12">
							<efInputPassword
								v-model="data.password"
								label="pages.auth.login.password"
								autocomplete="current-password"
								hide-toggle
								required
							/>
							<p class="small mb-0 float-end">
								<router-link to="forgot-password">{{
									t('pages.auth.login.password_reset.link')
								}}</router-link>
							</p>
						</div>

						<div class="col-12 mt-0">
							<div class="form-check form-switch">
								<input
									v-model="data.remember"
									class="form-check-input"
									type="checkbox"
									role="switch"
									name="remember"
									value="true"
									id="rememberMe"
								/>
								<label class="form-check-label" for="rememberMe">{{
									t('pages.auth.login.remember_me')
								}}</label>
							</div>
						</div>

						<div class="col-12">
							<efPromiseButton
								:callback="login"
								class="w-100"
								btnClass="btn-primary w-100"
								:disabled="!isValid"
							>
								{{ t('pages.auth.login.sign_in') }}
							</efPromiseButton>
						</div>

						<div v-if="selfRegistrationEnabled" class="col-12">
							<p class="small mb-0">
								<span>{{ t('pages.auth.login.register.text') }}&nbsp;</span>
								<a href="/register">{{ t('pages.auth.login.register.link') }}</a>
							</p>
						</div>

						<div class="divider-wrapper">
							<span class="divider">{{ t('pages.auth.login.social.divider') }}</span>
						</div>

						<template v-for="(service, ind) in oauth" :key="ind">
							<div v-if="service.id != null" class="col-12">
								<efPromiseButton
									:callback="() => oauth_login(service)"
									class="w-100"
									btnClass="btn-outline-secondary btn-social w-100"
								>
									<img
										class="social-logo me-2"
										:src="`/img/logos-sso/${service.icon}`"
										:alt="`logo-ssp-${service.key}`"
									/>
									<span>{{ t(`pages.auth.login.social.${service.key}`) }}</span>
								</efPromiseButton>
							</div>
						</template>
					</template>

					<template v-else>
						<div v-if="step === 'two_factor_setup'" class="col-12">
							<TwoFactorEnrollment v-if="enrollment" :enrollment="enrollment" />
							<div v-else-if="!error" class="d-flex justify-content-center py-5">
								<div class="spinner-border text-secondary" role="status"></div>
							</div>
						</div>

						<div class="col-12">
							<label for="two-factor-code" class="form-label d-block text-center">
								{{ t('components.two_factor.code') }}
							</label>
							<efOTPInput id="two-factor-code" v-model="code" :length="6" numeric />
						</div>

						<!-- Cancel is type=button so that Enter in the code field still reaches Continue,
						     which is the only submit button left in the form. -->
						<div class="col-12 d-flex gap-2">
							<button type="button" class="btn btn-secondary flex-fill" @click="cancelTwoFactor()">
								{{ t('common.cancel') }}
							</button>
							<efPromiseButton
								:callback="submitCode"
								class="flex-fill"
								btnClass="btn-primary w-100"
								:disabled="code.length !== 6 || (step === 'two_factor_setup' && !enrollment)"
							>
								{{ t('pages.auth.login.two_factor.submit') }}
							</efPromiseButton>
						</div>
					</template>
				</form>
			</efForm>
		</div>
	</div>
</template>

<style lang="scss">
.divider-wrapper:last-child {
	display: none;
}

/* btn-outline-secondary as it is, without the theme's gradient */
.btn-social {
	--bs-gradient: none;
}

.social-logo {
	height: 20px;
	display: inline-block;
	vertical-align: text-bottom;
}
</style>
